import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Typography } from '@material-ui/core';
import FormSpinner from 'components/FormSpinner';
import { notify } from 'reducers/NotifierReducer';
import { RMSButton } from '../../shared/components/buttons';
import { formatDate } from '../../shared/utility/dateFormatter';
import DATE_FORMATS from '../../shared/constants/dateFormats';
import useUserSignature from '../hooks/useUserSignature';
import SetupSignatureDialog from '../components/SetupSignatureDialog';
import ChangeSignatureDrawDialog from '../components/ChangeSignatureDrawDialog';
import ChangePinDialog from '../components/ChangePinDialog';
import PinEntryDialog from '../components/PinEntryDialog';
import { verifyPin, deleteSignature } from '../services/userSignatureService';

const ManageSignaturePage = () => {
  const dispatch = useDispatch();
  const { signature, signatureLoading, refetchSignature } = useUserSignature();

  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [changePinDialogOpen, setChangePinDialogOpen] = useState(false);
  const [changeSignatureVerifyOpen, setChangeSignatureVerifyOpen] = useState(false);
  const [changeSignatureDrawOpen, setChangeSignatureDrawOpen] = useState(false);
  const [deleteVerifyOpen, setDeleteVerifyOpen] = useState(false);
  const [verifiedPin, setVerifiedPin] = useState('');

  const closeChangeSignatureDraw = useCallback(() => {
    setChangeSignatureDrawOpen(false);
    setVerifiedPin('');
  }, []);

  const handleChangeSignatureVerified = useCallback((pin) => {
    setVerifiedPin(pin);
    setChangeSignatureVerifyOpen(false);
    setChangeSignatureDrawOpen(true);
  }, []);

  const handleDeleteConfirmed = useCallback(() => {
    dispatch(notify('Signature deleted successfully', 'success'));
    setDeleteVerifyOpen(false);
    refetchSignature();
  }, [dispatch, refetchSignature]);

  return (
    <div className="manage-signature">
      {signatureLoading && <FormSpinner />}

      {signature?.hasSignature ? (
        <div className="manage-signature__current">
          <img
            src={signature.signatureData}
            alt="Your signature"
            className="manage-signature__preview"
          />

          <Typography className="manage-signature__meta">
            Last updated:{' '}
            {formatDate(signature.Updated || signature.Created, DATE_FORMATS.DISPLAY_DATE_TIME)}
          </Typography>

          <div className="manage-signature__actions">
            <RMSButton
              label="Change Signature"
              variant="outlined"
              onClick={() => setChangeSignatureVerifyOpen(true)}
            />
            <RMSButton
              label="Change PIN"
              variant="outlined"
              onClick={() => setChangePinDialogOpen(true)}
            />
            <RMSButton
              label="Delete Signature"
              variant="outlined"
              color="secondary"
              onClick={() => setDeleteVerifyOpen(true)}
            />
          </div>
        </div>
      ) : (
        !signatureLoading && (
          <div className="manage-signature__empty">
            <Typography>You have not set up a signature yet.</Typography>
            <RMSButton
              label="Set Up Signature"
              color="primary"
              onClick={() => setSetupDialogOpen(true)}
            />
          </div>
        )
      )}

      {setupDialogOpen && (
        <SetupSignatureDialog
          open={setupDialogOpen}
          onClose={() => setSetupDialogOpen(false)}
          onSaved={refetchSignature}
        />
      )}

      {changePinDialogOpen && (
        <ChangePinDialog open={changePinDialogOpen} onClose={() => setChangePinDialogOpen(false)} />
      )}

      {changeSignatureVerifyOpen && (
        <PinEntryDialog
          open={changeSignatureVerifyOpen}
          title="Verify PIN"
          description="Enter your current PIN to change your signature."
          submitLabel="Verify"
          onClose={() => setChangeSignatureVerifyOpen(false)}
          onSubmit={verifyPin}
          onVerified={handleChangeSignatureVerified}
        />
      )}

      {changeSignatureDrawOpen && (
        <ChangeSignatureDrawDialog
          open={changeSignatureDrawOpen}
          pin={verifiedPin}
          onClose={closeChangeSignatureDraw}
          onSaved={refetchSignature}
        />
      )}

      {deleteVerifyOpen && (
        <PinEntryDialog
          open={deleteVerifyOpen}
          title="Delete Signature"
          description="Enter your PIN to confirm deleting your signature."
          submitLabel="Delete"
          onClose={() => setDeleteVerifyOpen(false)}
          onSubmit={deleteSignature}
          onVerified={handleDeleteConfirmed}
        />
      )}
    </div>
  );
};

export default ManageSignaturePage;
